import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Map<number, Promise<void>>} */
  #pendingPages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #renderingNextPage = false;

  /** @type {boolean} */
  #renderingPreviousPage = false;

  /** @type {number} */
  #filterVersion = 0;

  /** @type {boolean} */
  #filterUpdating = false;

  /** @type {boolean} */
  #scrollCheckScheduled = false;

  connectedCallback() {
    const url = new URL(window.location.href);
    if (this.getAttribute('infinite-scroll') === 'true' && url.pathname.startsWith('/collections/') && url.searchParams.has('page')) {
      url.searchParams.delete('page');
      window.location.replace(url.toString());
      return;
    }

    super.connectedCallback();

    this.#initializeAspectRatioHelper();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    if (this.getAttribute('infinite-scroll') === 'true') {
      window.addEventListener('scroll', this.#scheduleScrollCheck, { passive: true });
      window.addEventListener('resize', this.#scheduleScrollCheck);
      this.#scheduleScrollCheck();
    }

    if (this.getAttribute('infinite-scroll') === 'true' && this.refs.grid && !this.refs.grid.querySelector('[ref="cards[]"]')) {
      this.#recoverEmptyPage();
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('scroll', this.#scheduleScrollCheck);
    window.removeEventListener('resize', this.#scheduleScrollCheck);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#filterUpdating = false;
    this.infinityScrollObserver?.disconnect();
    this.#observeViewMore();
    this.#scheduleScrollCheck();
    if (this.getAttribute('infinite-scroll') === 'true' && this.refs.grid && !this.refs.grid.querySelector('[ref="cards[]"]')) {
      this.#recoverEmptyPage();
    } else {
      this.#fetchPage('next');
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (this.#recoveringEmptyPage || this.#filterUpdating) continue;
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #scheduleScrollCheck = () => {
    if (this.#scrollCheckScheduled || this.getAttribute('infinite-scroll') !== 'true') return;
    this.#scrollCheckScheduled = true;
    requestAnimationFrame(() => {
      this.#scrollCheckScheduled = false;
      if (!this.isConnected || this.#renderingNextPage || this.#recoveringEmptyPage || this.#filterUpdating) return;
      const sentinel = this.refs.viewMoreNext;
      const nextPage = this.#getPage('next');
      if (!sentinel || !this.#shouldUsePage(nextPage)) return;
      const bounds = sentinel.getBoundingClientRect();
      if (bounds.top <= window.innerHeight + 100 && bounds.bottom >= -100) this.#renderNextPage();
    });
  };

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;
    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (_error) {
      // A later scroll can retry this page.
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    if (this.pages.has(pageNumber)) return;
    const pending = this.#pendingPages.get(pageNumber);
    if (pending) return pending;

    const filterVersion = this.#filterVersion;
    const request = sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url).then((pageContent) => {
      if (filterVersion === this.#filterVersion) {
        this.pages.set(pageNumber, pageContent);
      }
    });
    this.#pendingPages.set(pageNumber, request);
    try {
      await request;
    } finally {
      if (this.#pendingPages.get(pageNumber) === request) {
        this.#pendingPages.delete(pageNumber);
      }
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#renderingNextPage || this.#filterUpdating) return false;
    this.#renderingNextPage = true;
    const filterVersion = this.#filterVersion;
    let appendedPage = false;

    try {
      let nextPage = this.#getPage('next');
      while (nextPage && this.#shouldUsePage(nextPage)) {
        let items = this.#getGridForPage(nextPage.page);
        if (!items) {
          await this.#fetchSpecificPage(nextPage.page, nextPage.url);
          items = this.#getGridForPage(nextPage.page);
        }
        if (filterVersion !== this.#filterVersion || this.#filterUpdating || grid !== this.refs.grid || !items) return false;

        grid.dataset.lastLoadedPage = String(nextPage.page);
        if (items.length) {
          grid.append(...items);
          appendedPage = true;
          this.#initializeAspectRatioHelper();
          this.#aspectRatioHelper?.processNewElements();
          requestIdleCallback(() => this.#fetchPage('next'));
          return true;
        }
        nextPage = this.#getPage('next');
      }
      return false;
    } catch (_error) {
      return null;
    } finally {
      this.#renderingNextPage = false;
      if (appendedPage) this.#scheduleScrollCheck();
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid || this.#renderingPreviousPage || this.#filterUpdating) return false;
    this.#renderingPreviousPage = true;
    const filterVersion = this.#filterVersion;

    try {
      let previousPage = this.#getPage('previous');
      while (previousPage && this.#shouldUsePage(previousPage)) {
        let items = this.#getGridForPage(previousPage.page);
        if (!items) {
          await this.#fetchSpecificPage(previousPage.page, previousPage.url);
          items = this.#getGridForPage(previousPage.page);
        }
        if (filterVersion !== this.#filterVersion || this.#filterUpdating || grid !== this.refs.grid || !items) return false;

        grid.dataset.firstLoadedPage = String(previousPage.page);
        if (items.length) {
          const scrollTop = window.scrollY;
          const firstElement = grid.firstElementChild;
          const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;
          grid.prepend(...items);
          this.#initializeAspectRatioHelper();
          this.#aspectRatioHelper?.processNewElements();

          if (firstElement) {
            const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: scrollTop + newHeight - oldHeight, behavior: 'instant' });
          }
          requestIdleCallback(() => this.#fetchPage('previous'));
          return true;
        }
        previousPage = this.#getPage('previous');
      }
      return false;
    } catch (_error) {
      return null;
    } finally {
      this.#renderingPreviousPage = false;
    }
  }

  /** @type {boolean} */
  #recoveringEmptyPage = false;

  #initializeAspectRatioHelper() {
    if (this.#aspectRatioHelper) return;
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    }
  }

  async #recoverEmptyPage() {
    if (this.#recoveringEmptyPage) return;
    this.#recoveringEmptyPage = true;
    const filterVersion = this.#filterVersion;
    try {
      const nextResult = await this.#renderNextPage();
      if (filterVersion !== this.#filterVersion) return;
      if (nextResult === true) return;
      const previousResult = await this.#renderPreviousPage();
      if (filterVersion !== this.#filterVersion) return;
      if (nextResult === false && previousResult === false) {
        if (!this.refs.grid?.querySelector('[ref="cards[]"]')) {
          this.refs.visibleEmpty?.removeAttribute('hidden');
        }
      }
    } finally {
      this.#recoveringEmptyPage = false;
      if (filterVersion !== this.#filterVersion && this.refs.grid && !this.refs.grid.querySelector('[ref="cards[]"]')) {
        this.#recoverEmptyPage();
      }
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards, grid } = this.refs;
    const isPrevious = type === 'previous';
    const targetCard = Array.isArray(cards) ? cards[isPrevious ? 0 : cards.length - 1] : undefined;
    const loadedPage = isPrevious ? grid?.dataset.firstLoadedPage : grid?.dataset.lastLoadedPage;
    const currentCardPage = Number(loadedPage || targetCard?.dataset.page || 1);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#filterVersion += 1;
    this.#filterUpdating = true;
    this.pages.clear();
    this.#pendingPages.clear();
    this.refs.visibleEmpty?.setAttribute('hidden', '');
  };
}
